import type { ReactNode } from "react";
import styled from "styled-components";
import { Button, Dropdown, Tooltip } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, MoreOutlined, OrderedListOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { PostCase, postCaseDisplayName } from "../../api/postCaseAPI";

interface Props {
  cases: PostCase[];
  activeCaseId: number | null; // null = 전체 보기
  hasEditAuth: boolean;
  onSelectCase: (postCaseId: number | null) => void; // null 이면 전체
  onAddCase: () => void;
  onEditCase: (postCase: PostCase) => void;
  onDeleteCase: (postCase: PostCase) => void;
  //Case 가 2개 이상일 때만 노출되는 순서 변경. 없으면 버튼 미노출
  onReorderCases?: () => void;
  rightAction?: ReactNode; // 우측 끝에 배치할 액션 (예: 상하/좌우 배치 토글)
}

const PostCaseSelector = ({
  cases,
  activeCaseId,
  hasEditAuth,
  onSelectCase,
  onAddCase,
  onEditCase,
  onDeleteCase,
  onReorderCases,
  rightAction,
}: Props) => {
  if (cases.length === 0) {
    if (!hasEditAuth) {
      if (!rightAction) return null;
      return (
        <Container>
          <TopRow>
            <RightActionSlot>{rightAction}</RightActionSlot>
          </TopRow>
        </Container>
      );
    }
    return (
      <Container>
        <TopRow>
          <Button icon={<PlusOutlined />} size="small" onClick={onAddCase}>
            Case 추가
          </Button>
          {rightAction && <RightActionSlot>{rightAction}</RightActionSlot>}
        </TopRow>
      </Container>
    );
  }

  const activeCase = activeCaseId !== null ? cases.find(c => c.postCaseId === activeCaseId) : null;
  const hasActiveCaseInfo =
    activeCase && (activeCase.organ || activeCase.gender || activeCase.age || activeCase.note);

  return (
    <Container>
      <TopRow $hasInfo={!!hasActiveCaseInfo}>
        <Label>Case</Label>
        <ButtonRow>
          {/* 전체 보기 */}
          <CaseButton
            type={activeCaseId === null ? "primary" : "default"}
            onClick={() => onSelectCase(null)}
            size="small"
          >
            전체
          </CaseButton>

          {cases.map((pc, idx) => {
            const displayName = postCaseDisplayName(pc, idx);
            const subInfo = [pc.gender, pc.age].filter(Boolean).join(" / ");
            const tooltipTitle = pc.organ
              ? `${pc.organ}${subInfo ? ` (${subInfo})` : ""}`
              : subInfo || displayName;

            const menuItems: MenuProps["items"] = [
              {
                key: "edit",
                label: "수정",
                icon: <EditOutlined />,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  onEditCase(pc);
                },
              },
              {
                key: "delete",
                label: "삭제",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: ({ domEvent }) => {
                  domEvent.stopPropagation();
                  onDeleteCase(pc);
                },
              },
            ];

            return (
              <CaseButtonWrap key={pc.postCaseId}>
                <Tooltip title={tooltipTitle} placement="bottom">
                  <CaseButton
                    type={activeCaseId === pc.postCaseId ? "primary" : "default"}
                    onClick={() => onSelectCase(pc.postCaseId)}
                    size="small"
                  >
                    {displayName}
                  </CaseButton>
                </Tooltip>
                {hasEditAuth && (
                  <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
                    <MoreButton
                      type="text"
                      size="small"
                      icon={<MoreOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Dropdown>
                )}
              </CaseButtonWrap>
            );
          })}

          {hasEditAuth && (
            <Tooltip title="Case 추가">
              <AddButton icon={<PlusOutlined />} size="small" onClick={onAddCase} />
            </Tooltip>
          )}

          {/* 순서를 바꿀 게 있을 때(2개 이상)만 노출 */}
          {hasEditAuth && onReorderCases && cases.length > 1 && (
            <Tooltip title="Case 순서 변경">
              <OrderButton
                icon={<OrderedListOutlined />}
                size="small"
                onClick={onReorderCases}
              />
            </Tooltip>
          )}
        </ButtonRow>
        {rightAction && <RightActionSlot>{rightAction}</RightActionSlot>}
      </TopRow>

      {hasActiveCaseInfo && (
        <InfoRow>
          {activeCase.organ && (
            <InfoItem>
              <InfoLabel>Organ:</InfoLabel> {activeCase.organ}
            </InfoItem>
          )}
          {activeCase.gender && (
            <InfoItem>
              <InfoLabel>Gender:</InfoLabel> {activeCase.gender}
            </InfoItem>
          )}
          {activeCase.age && (
            <InfoItem>
              <InfoLabel>Age:</InfoLabel> {activeCase.age}
            </InfoItem>
          )}
          {/* note 는 라벨 없이 값만 표시 */}
          {activeCase.note && <NoteItem>{activeCase.note}</NoteItem>}
        </InfoRow>
      )}
    </Container>
  );
};

export default PostCaseSelector;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  flex-shrink: 0;
`;

const TopRow = styled.div<{ $hasInfo?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 8px;
  /* 최상위는 줄바꿈하지 않음 → 라벨(좌)과 우측 액션(토글)은 고정, 가운데 목록만 wrap */
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 16px 10px 16px;
  font-size: 0.85rem;
  color: #595959;
  flex-wrap: wrap;
`;

const InfoItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-weight: 600;
  color: #8c8c8c;
`;

// note 는 "Note:" 같은 접두어 없이 입력된 값만 표시
const NoteItem = styled.span`
  color: #595959;
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 0;
`;

const Label = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: #595959;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  flex-shrink: 0;
  /* 목록이 여러 줄로 wrap 될 때 첫 줄 버튼과 세로 정렬 맞춤 */
  padding-top: 5px;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  /* 라벨과 우측 액션 사이의 가변 영역을 차지하고, 목록이 넘치면 이 안에서만 줄바꿈 */
  flex: 1;
  min-width: 0;
`;

// TopRow 가장 오른쪽에 붙는 액션 슬롯 (margin-left: auto 로 우측 끝 정렬)
const RightActionSlot = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const CaseButtonWrap = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  overflow: hidden;
`;

const CaseButton = styled(Button)`
  font-weight: 500;
  min-width: 64px;
`;

const MoreButton = styled(Button)`
  padding: 0 4px !important;
  height: 24px !important;
  margin-left: 2px;
  color: #8c8c8c;

  &:hover {
    color: #1890ff;
    background-color: transparent !important;
  }
`;

const AddButton = styled(Button)`
  border-style: dashed;
`;

const OrderButton = styled(Button)`
  color: #8c8c8c;

  &:hover {
    color: #1890ff;
  }
`;
